import type { NavigationContainerRefWithCurrent } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import type { Href, UnknownInputParams } from 'expo-router';
import { router } from 'expo-router';
import { Platform } from 'react-native';

import { routes } from '@/constants/routes';
import { isWidget, widgetEntryHref } from '@/utils/isWidget';

import { Logger } from './logging';

export function hrefToString(href: Href): string {
  if (typeof href === 'string') return href;

  const { pathname, params } = href as {
    pathname: string;
    params?: UnknownInputParams;
  };

  if (!params || Object.keys(params).length === 0) return pathname;

  const usedInPath = new Set<string>();
  const resolvedPath = pathname.replace(/\[(\w+)\]/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined || value === null) return `[${key}]`;
    usedInPath.add(key);
    return String(Array.isArray(value) ? value[0] : value);
  });

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (usedInPath.has(key) || value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `${resolvedPath}?${query}` : resolvedPath;
}

// The keys the loader puts on the iframe URL (widget/src/appUrl.ts). Only
// these are copied so a return URL never inherits the Stripe params of an
// earlier return when the widget pays again in the same session.
const WIDGET_ENTRY_QUERY_KEYS = ['platform', 'brand', 'viewMode'];

/**
 * Return URL handed to Stripe for redirect-based payment methods (Cash App,
 * Klarna / Affirm / Afterpay). Resolves to the app's custom scheme on native
 * (`squire://…`) and to the page origin on web, so the provider always lands
 * back in the running app. Universal (https) links are deliberately avoided:
 * iOS only opens them in-app when the device trusts the domain for the
 * *installed* bundle id, which the dev client is not (SQR-15312).
 *
 * Inside the widget the return URL also carries the iframe's entry query:
 * expo-router drops it on the first client-side navigation, and without
 * `platform=widget` the return boot would not detect the widget (SQR-13815).
 */
export function getPaymentReturnUrl(href: Href): string {
  const returnUrl = Linking.createURL(hrefToString(href).slice(1)); // drop leading "/"
  if (!isWidget || !widgetEntryHref) return returnUrl;

  const url = new URL(returnUrl);
  const entryParams = new URL(widgetEntryHref).searchParams;
  for (const key of WIDGET_ENTRY_QUERY_KEYS) {
    const value = entryParams.get(key);
    if (value !== null && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

export function goBackWithFallback(
  backRoute: Href,
  fallbackRoute: Href = routes.home,
) {
  if (router.canGoBack()) {
    return router.back();
  }
  if (router.canDismiss()) {
    return router.dismissTo(backRoute);
  }

  if (Platform.OS === 'web' && window.history.length > 1) {
    window.history.back();
    return;
  }

  Logger.warn('Back fell back to a replace', {
    fallbackRoute,
  });
  router.replace(fallbackRoute);
}

// window.history.length > 1 only means the tab's session history has more
// than one entry total — it says nothing about whether there's an entry
// BEHIND the current one specifically (the current entry can already be
// first, with only forward entries ahead). In that case history.back() is a
// no-op and never fires popstate, so a popstate-only wait would hang forever.
const POPSTATE_FALLBACK_TIMEOUT_MS = 300;

function dismissOrReplace(backRoute: Href) {
  if (router.canDismiss()) {
    return router.dismiss();
  }

  Logger.warn('Back fell back to a replace', {
    backRoute,
  });
  router.replace(backRoute);
}

export function goBackInBookingFlow(
  backRoute: Href = routes.home,
  skipBackToPreviousApp: boolean = false,
) {
  // The iframe shares its session history with the host page, so
  // window.history.back() could navigate the customer's site.
  if (isWidget) {
    if (router.canGoBack()) {
      return router.back();
    }
    return dismissOrReplace(backRoute);
  }

  if (
    !skipBackToPreviousApp &&
    Platform.OS === 'web' &&
    window.history.length > 1
  ) {
    // A screen that mutates its own URL after mount (e.g. appending
    // ?cart=... once a cart is created) pushes a same-route history entry.
    // history.back() then lands back on that entry instead of leaving the
    // screen, silently stranding the user (SQR-15890). Detect that by
    // comparing the pathname before/after and fall through to the
    // dismiss/replace fallback when it didn't actually change.
    const pathnameBeforeBack = window.location.pathname;
    // canGoBack only counts this origin's entries: true means a same-origin
    // entry is behind, so no timeout race is needed. False can still mean the
    // referring site is behind, so history.back() is tried with the timeout.
    const canGoBack = (
      window as Window & { navigation?: { canGoBack?: boolean } }
    ).navigation?.canGoBack;
    let settled = false;

    const handlePopState = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      window.removeEventListener('popstate', handlePopState);
      if (window.location.pathname === pathnameBeforeBack) {
        dismissOrReplace(backRoute);
      }
    };

    // history.back() gives no synchronous/promise-based signal of whether it
    // actually did anything, so without canGoBack race the popstate listener
    // against a timeout in case back() turned out to be a no-op.
    const timeoutId = canGoBack
      ? undefined
      : setTimeout(() => {
          if (settled) return;
          settled = true;
          window.removeEventListener('popstate', handlePopState);
          dismissOrReplace(backRoute);
        }, POPSTATE_FALLBACK_TIMEOUT_MS);

    window.addEventListener('popstate', handlePopState);
    window.history.back();
    return;
  }

  dismissOrReplace(backRoute);
}

/**
 * Lands on view-reservation with a clean `[Home, view-reservation]` stack in a
 * single dispatch, so the header's close button (dismissAll) always returns to
 * Home. Every post-payment entry point must use this rather than
 * `router.replace(routes.viewReservation(...))`: a replace keeps the booking
 * history (shop → services → schedule) underneath, and dismissAll then pops to
 * the shop page instead of Home (SQR-15312).
 */
export function resetToViewReservation(
  navigationRef: NavigationContainerRefWithCurrent<object>,
  params: { saleOrderId: string; bnpl?: 'true' },
) {
  navigationRef.reset({
    index: 1,
    routes: [
      { name: 'index' },
      {
        name: 'booking',
        state: {
          index: 0,
          routes: [
            {
              name: 'view-reservation',
              params: { screen: '[saleOrderId]/index', params },
            },
          ],
        },
      },
    ],
  });
}

// Same single-dispatch reset as resetToViewReservation: on web, dismissAll()
// followed by push() drops the push and strands the user on Home.
export function resetToStandbyWaitlistJoined(
  navigationRef: NavigationContainerRefWithCurrent<object>,
  params: Parameters<typeof routes.standbyWaitlistJoined>[0],
) {
  const { pathname } = routes.standbyWaitlistJoined(params) as {
    pathname: string;
  };
  navigationRef.reset({
    index: 1,
    routes: [
      { name: 'index' },
      {
        name: 'booking',
        state: {
          index: 0,
          // The booking stack names a screen by its file path under booking/.
          routes: [
            { name: `${pathname.replace('/booking/', '')}/index`, params },
          ],
        },
      },
    ],
  });
}
